import { t, useStoreLanguage, getDirection } from "../features/store-language/i18n.ts";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useSpring } from "framer-motion";
import { HamburgerMenuIcon } from "@solar-icons/react/linear/hamburger-menu";
import { BoxMinimalisticIcon } from "@solar-icons/react/linear/box-minimalistic";
import {
  AppstoreOutlined,
} from "@ant-design/icons";
import {
  UserCircle,
} from "@phosphor-icons/react";
import {
  Dropdown,
  Tooltip,
} from "antd";
import AuthenticatedImage from "../components/shared/AuthenticatedImage.tsx";
import { getTenantInfo, getStoreCategories } from "../components/Shop/storefrontService.ts";
import { agencyLogout } from "../components/Shop/Auth/agencyIdentityService.ts";
import { PendingRing } from "../components/shared/LoadingRing.jsx";
import { PlatformAdminIcon } from "../features/platform-admin/PlatformAdminIcon.tsx";
import "../features/platform-admin/platform-admin-tokens.css";
import "./PortalShell/PortalAccountMenu.css";
import AppFeedback from "../components/shared/AppFeedback.tsx";
import {
  clearStaffShopSession,
} from "../components/Shop/Orders/orderService.ts";
import {
  getShopOrdersPath,
  isAgencyShopSession,
} from "../components/Shop/Orders/shopSession.ts";
import ShopTopMenu from "./ShopTopMenu.tsx";
import RajBrandFooter from "../features/raj-brand/RajBrandFooter.tsx";
import ShopBottomNav from "./ShopBottomNav.tsx";
import RetainedShowroom from "../features/showroom/RetainedShowroom.tsx";
import { useActiveShowroomSession } from "../features/showroom/ActiveShowroomSession.tsx";
import ShopHeaderNavigation from "./ShopHeaderNavigation.tsx";
import "./ShopLayout.css";
import "./ShopHeaderNavigation.css";
import StorefrontSeo from "../components/Shop/seo/StorefrontSeo.tsx";
import { buildCategoryPath } from "../components/Shop/categoryUrl.ts";
import { buildProductPath, buildVariantPath } from "../components/Shop/productUrl.ts";

import ShopSearch from "../components/Shop/ShopSearch.tsx";
import { searchStoreCatalog, type StoreSearchResult } from "../components/Shop/storeSearchService.ts";

interface CompanyInfoData {
  title?: string;
  fileId?: string | number;
}

export interface ShopLayoutOutletContext {
  mobileNavigation: ReactNode;
  companyInfo: CompanyInfoData | null;
  logoutPending: boolean;
  handleLogout: () => Promise<void>;
}

function ShopLayout() {
  const storeLanguage = useStoreLanguage();
  const { active: activeShowroom } = useActiveShowroomSession();
  const bottomNavPosition = useSpring(0, { stiffness: 380, damping: 28, mass: 0.8 });
  const searchGeneration = useRef(0);
  const navigate = useNavigate();
  const location = useLocation();
  const isCustomerPanel =
    location.pathname === "/account" || location.pathname.startsWith("/account/");
  const isShowroom = location.pathname === "/showroom" || location.pathname.startsWith("/showroom/");
  const hasOwnHeader = isCustomerPanel || isShowroom;
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [menuVisible, setMenuVisible] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(function () {
    return isAgencyShopSession();
  });
  const [categories, setCategories] = useState<any[]>([]);
  const [searchResults, setSearchResults] = useState<StoreSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const [logoutPending, setLogoutPending] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const logoutInFlight = useRef(false);
  const shopLayoutRef = useRef<HTMLDivElement | null>(null);
  const shopHeaderRef = useRef<HTMLElement | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const invalidateSearch = useCallback(function () {
    searchGeneration.current++;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
  }, []);

  useEffect(function () {
    const desktopViewport = window.matchMedia("(min-width: 1025px)");

    function closeMobileMenu(event: MediaQueryListEvent) {
      if (event.matches) setMenuVisible(false);
    }

    desktopViewport.addEventListener("change", closeMobileMenu);
    return function () {
      desktopViewport.removeEventListener("change", closeMobileMenu);
    };
  }, []);

  useEffect(function () {
    const root = document.getElementById("root");
    document.body.classList.add("shop-page-active");
    root?.classList.add("shop-page-active");

    return function () {
      document.body.classList.remove("shop-page-active");
      root?.classList.remove("shop-page-active");
    };
  }, [storeLanguage]);

  useEffect(function () {
    const layout = shopLayoutRef.current;
    const header = shopHeaderRef.current;
    if (!layout || !header) {
      return undefined;
    }

    function updateStickyHeaderHeight() {
      const headerHeight = Math.ceil(header.getBoundingClientRect().height);
      layout.style.setProperty(
        "--shop-sticky-header-height",
        `${headerHeight}px`,
      );
    }

    updateStickyHeaderHeight();
    const resizeObserver =
      typeof ResizeObserver === "function"
        ? new ResizeObserver(updateStickyHeaderHeight)
        : null;
    resizeObserver?.observe(header);
    window.addEventListener("resize", updateStickyHeaderHeight);

    return function () {
      resizeObserver?.disconnect();
      window.removeEventListener("resize", updateStickyHeaderHeight);
      layout.style.removeProperty("--shop-sticky-header-height");
    };
  }, [storeLanguage, hasOwnHeader]);

  const loadCompanyInfo = useCallback(
    async function () {
      void storeLanguage; // Locale is part of this read/translation cache key.
      try {
        const response = await getTenantInfo();
        setCompanyInfo(response);
        localStorage.setItem("company", JSON.stringify(response));
      } catch (error) {
        console.error("Error loading company info:", error);
      }
    },
    [storeLanguage],
  );

  const loadUserInfo = useCallback(
    async function () {
      setIsLoggedIn(isAgencyShopSession());
    },
    [],
  );

  const loadNavigation = useCallback(
    async function () {
      void storeLanguage; // Locale is part of this read/translation cache key.
      if (hasOwnHeader) return;
      if (!isAgencyShopSession()) {
        const publicCategories = await getStoreCategories();
        setCategories(Array.isArray(publicCategories) ? publicCategories : []);
        return;
      }
      try {
        const categoryResponse = await getStoreCategories();
        setCategories(categoryResponse || []);
      } catch (error) {
        console.error("Error loading shop navigation:", error);
      }
    },
    [storeLanguage, hasOwnHeader],
  );

  useEffect(
    function () {
      void loadCompanyInfo();

      function handleLogin() {
        void loadCompanyInfo();
      }

      window.addEventListener("auth:login", handleLogin);
      return function () {
        window.removeEventListener("auth:login", handleLogin);
      };
    },
    [loadCompanyInfo],
  );

  useEffect(
    function () {
      if (!hasOwnHeader) {
        void loadUserInfo();
        void loadNavigation();
      }

      function handleLogin() {
        if (!hasOwnHeader) {
          void loadUserInfo();
          void loadNavigation();
        }
      }

      window.addEventListener("auth:login", handleLogin);
      return function () {
        window.removeEventListener("auth:login", handleLogin);
      };
    },
    [hasOwnHeader, loadNavigation, loadUserInfo],
  );

  const performHotSearch = useCallback(
    async function (searchTerm: string) {
      void storeLanguage; // Locale is part of this read/translation cache key.
      const trimmedTerm = searchTerm.trim();
      const generation = ++searchGeneration.current;
      if (trimmedTerm.length < 3) {
        setSearchResults([]);
        return;
      }
      try {
        setSearchLoading(true);
        const response = await searchStoreCatalog(trimmedTerm);
        if (generation === searchGeneration.current) setSearchResults(response);
      } catch (error) {
        console.error("Error performing hot search:", error);
        if (generation === searchGeneration.current) { setSearchResults([]); setSearchFailed(true); }
      } finally {
        if (generation === searchGeneration.current) { setSearchLoading(false); }
      }
    },
    [storeLanguage],
  );

  function handleSearchChange(value: string) {
    searchGeneration.current++;
    setSearchQuery(value);
    setSearchResults([]);
    setSearchFailed(false);
    setSearchLoading(value.trim().length >= 3);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    if (value.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    searchTimeoutRef.current = setTimeout(function () {
      void performHotSearch(value);
    }, 300);
  }

  function clearSearch() {
    searchGeneration.current++;
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    setSearchQuery("");
    setSearchResults([]);
    setSearchLoading(false);
    setSearchFailed(false);
  }

  function handleSearch(value: string) {
    const trimmedValue = value.trim();
    if (!trimmedValue) {
      return;
    }
    clearSearch();
    navigate(`/search?q=${encodeURIComponent(trimmedValue)}`);
  }

  function handleSearchSelect(result: StoreSearchResult) {
    clearSearch();
    navigate(result.kind === "variant" && result.variantAddressTitle
      ? buildVariantPath(result.masterSlug, result.variantAddressTitle)
      : buildProductPath(result.masterSlug));
  }

  useEffect(function () {
    invalidateSearch();
    setSearchQuery("");
    setSearchResults([]);
    setSearchLoading(false);
    setSearchFailed(false);
    return invalidateSearch;
  }, [storeLanguage, location.pathname, location.search, invalidateSearch]);

  async function handleLogout() {
    if (logoutInFlight.current) return;
    logoutInFlight.current = true;
    setAccountMenuOpen(false);
    const confirmation = await AppFeedback.confirmDestructive({
      title: t("خروج از سامانه"),
      text: t("آیا می‌خواهید از پورتال نمایندگان {{value0}} خارج شوید؟", {
        value0: companyInfo?.title || t("شرکت"),
      }),
      confirmButtonText: t("خروج از سامانه"),
      cancelButtonText: t("انصراف"),
    });
    if (!confirmation.isConfirmed) {
      logoutInFlight.current = false;
      return;
    }
    setLogoutPending(true);
    try { await agencyLogout(); }
    catch (error) { AppFeedback.httpError(error); return; }
    finally { logoutInFlight.current = false; setLogoutPending(false); }
    const loginPath = "/login";
    localStorage.removeItem("jwt");
    localStorage.removeItem("actionTypes");
    localStorage.removeItem("authType");
    localStorage.removeItem("user");
    clearStaffShopSession();
    setIsLoggedIn(false);
    navigate(loginPath);
  }

  const navigationMenuItems = useMemo(
    function () {
      void storeLanguage; // Locale is part of this read/translation cache key.
      function categoryItem(category: any): import("./ShopTopMenu.tsx").ShopTopMenuItem {
        return {
          key: `category-${category.id}`,
          label: category.name,
          path: buildCategoryPath(category.slug),
          children: category.childCategories?.map(categoryItem),
        };
      }
      return categories.map(categoryItem);
    },
    [storeLanguage, categories],
  );

  const userMenuItems = [
    {
      key: "customer-panel",
      icon: <PlatformAdminIcon name="profile" />,
      label: t("پورتال من"),
      disabled: logoutPending,
      onClick: function () {
        navigate(getShopOrdersPath());
      },
    },
    ...(activeShowroom ? [{
      key: "showroom",
      icon: (
        <BoxMinimalisticIcon
          size={20}
          strokeWidth={1.7}
          aria-hidden="true"
        />
      ),
      label: t("نمایشگاه"),
      disabled: logoutPending,
      onClick: function () {
        setAccountMenuOpen(false);
        navigate("/showroom/");
      },
    }] : []),
    {
      key: "logout",
      icon: logoutPending ? <PendingRing /> : <PlatformAdminIcon name="logout" />,
      label: logoutPending ? t("در حال خروج…") : t("خروج"),
      disabled: logoutPending,
      onClick: handleLogout,
    },
  ];

  useLayoutEffect(function () {
    // Mobile scroll is owned by the shell; list history restores its saved offset afterwards.
    shopLayoutRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname]);

  const brandTitle = companyInfo?.title || t("کاتالوگ رَج");

  const mobileNavigation = (
    <ShopBottomNav
      position={bottomNavPosition}
      hidden={!hasOwnHeader && menuVisible}
      isLoggedIn={isLoggedIn}
      logoutPending={logoutPending}
      onLogout={handleLogout}
    />
  );

  return (
    <div
      className={`shop-layout${isCustomerPanel ? " shop-layout--customer-panel" : isShowroom ? " shop-layout--showroom" : ""}`}
      id="scrollableContainer"
      dir={getDirection()}
      ref={shopLayoutRef}
    >
      <StorefrontSeo />
      <RetainedShowroom
        active={isShowroom}
        companyInfo={companyInfo}
      />
      {!hasOwnHeader && (
        <>
          <header
            className="shop-header"
            ref={shopHeaderRef}
            aria-hidden={menuVisible ? true : undefined}
          >
            <div className="shop-container shop-header__inner">
              <button
                type="button"
                className="shop-icon-button shop-header__menu"
                aria-label={t("باز کردن منوی کاتالوگ")}
                aria-expanded={menuVisible}
                aria-controls="shop-top-menu"
                ref={menuTriggerRef}
                onClick={function () {
                  setMenuVisible(true);
                }}
              >
                <HamburgerMenuIcon
                  size={24}
                  strokeWidth={1.65}
                  aria-hidden="true"
                />
              </button>

              <Link
                to="/"
                className="shop-brand"
                aria-label={t("صفحه اصلی کاتالوگ")}
              >
                {companyInfo?.fileId ? (
                  <AuthenticatedImage
                    fileId={String(companyInfo.fileId)}
                    disablePreview={true}
                    originalImage={false}
                    className="shop-brand__image"
                    alt={brandTitle}
                  />
                ) : (
                  <span
                    className="shop-brand__mark"
                    aria-hidden="true"
                  >
                    <AppstoreOutlined />
                  </span>
                )}
              </Link>

              <ShopHeaderNavigation
                items={navigationMenuItems}
                currentPath={location.pathname}
              />

              <ShopSearch
                className="shop-search--mobile mobile-only"
                companyName={brandTitle}
                value={searchQuery}
                loading={searchLoading}
                failed={searchFailed}
                results={searchResults}
                onChange={handleSearchChange}
                onSearch={handleSearch}
                onSelect={handleSearchSelect}
              />

              <ShopSearch
                className="shop-search--desktop desktop-only"
                companyName={brandTitle}
                value={searchQuery}
                loading={searchLoading}
                failed={searchFailed}
                results={searchResults}
                onChange={handleSearchChange}
                onSearch={handleSearch}
                onSelect={handleSearchSelect}
              />

              <div className="shop-header__actions desktop-only">
                {isLoggedIn ? (
                  <Dropdown
                    menu={{ items: userMenuItems }}
                    placement={getDirection() === "rtl" ? "bottomLeft" : "bottomRight"}
                    trigger={["click"]}
                    overlayClassName="portal-account-menu shop-account-dropdown theme-light"
                    autoFocus
                    open={accountMenuOpen}
                    onOpenChange={setAccountMenuOpen}
                    disabled={logoutPending}
                  >
                    <Tooltip
                      title={accountMenuOpen ? null : t("پورتال من")}
                      trigger={["hover", "focus"]}
                    >
                      <button
                        type="button"
                        className="shop-account-button"
                        aria-label={t("منوی حساب کاربری")}
                        aria-haspopup="menu"
                        aria-expanded={accountMenuOpen}
                        aria-busy={logoutPending}
                        disabled={logoutPending}
                      >
                        {logoutPending ? (
                          <PendingRing />
                        ) : (
                          <PlatformAdminIcon
                            name="profile"
                            size={24}
                          />
                        )}
                        <span>{t("حساب کاربری")}</span>
                      </button>
                    </Tooltip>
                  </Dropdown>
                ) : (
                  <button
                    type="button"
                    className="shop-account-button"
                    aria-label={t("ورود یا ثبت‌نام")}
                    title={t("ورود یا ثبت‌نام")}
                    onClick={function () {
                      navigate("/login", {
                        state: {
                          returnTo: `${location.pathname}${location.search}`,
                        },
                      });
                    }}
                  >
                    <UserCircle
                      size={24}
                      weight="regular"
                      aria-hidden="true"
                    />
                    <span>{t("حساب کاربری")}</span>
                  </button>
                )}
              </div>
            </div>
          </header>

          <ShopTopMenu
            open={menuVisible}
            items={navigationMenuItems}
            currentPath={location.pathname}
            triggerRef={menuTriggerRef}
            onClose={function () {
              setMenuVisible(false);
            }}
          />

        </>
      )}
      {hasOwnHeader ? (
        <Outlet
          context={{ companyInfo, logoutPending, handleLogout, mobileNavigation } satisfies ShopLayoutOutletContext}
        />
      ) : (
        <main
          className="shop-main"
          aria-hidden={menuVisible ? true : undefined}
        >
          <div className="shop-container">
            <Outlet
              context={{ companyInfo, logoutPending, handleLogout, mobileNavigation } satisfies ShopLayoutOutletContext}
            />
          </div>
          <RajBrandFooter />
        </main>
      )}

      {isShowroom && <RajBrandFooter />}
      {!isCustomerPanel && mobileNavigation}
    </div>
  );
}

export default ShopLayout;
